import { useEffect, useCallback, useRef } from "react";
import { HiOutlineXMark, HiOutlineArrowDownTray, HiOutlineShare, HiOutlineDocumentText } from "react-icons/hi2";
import { FilePreviewSurface } from "../FilePreviewSurface";
import { formatBytes } from "../previewUtils";

export const FileViewer = ({ file, previewUrl, contentUrl, previewError, isOpen, onClose, onDownload, onShare, canShare }) => {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const backdropMouseDownRef = useRef(false);
  const previousOverflowRef = useRef("");

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  const handleDialogKeyDown = useCallback((e) => {
    if (e.key !== "Tab") return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusable = Array.from(
      modal.querySelectorAll(
        'button:not([disabled]), [href], iframe, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((node) => node instanceof HTMLElement && node.offsetParent !== null);

    if (focusable.length === 0) {
      e.preventDefault();
      modal.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
      return;
    }

    if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const handleBackdropMouseDown = useCallback((e) => {
    backdropMouseDownRef.current = e.target === e.currentTarget;
  }, []);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget && backdropMouseDownRef.current) {
        onClose();
      }
      backdropMouseDownRef.current = false;
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      previousOverflowRef.current = document.body.style.overflow;
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      window.requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflowRef.current;
      backdropMouseDownRef.current = false;
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !file) return null;

  return (
    <div
      className="fixed inset-0 z-toast flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Viewing ${file.original_name}`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onMouseDown={handleBackdropMouseDown}
        onClick={handleBackdropClick}
        tabIndex={-1}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative h-[90vh] w-[95vw] max-w-[1200px] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col"
        tabIndex={-1}
        onKeyDown={handleDialogKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-surface-border shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <HiOutlineDocumentText className="w-5 h-5 text-grey shrink-0" />
            <p className="text-sm font-medium text-darkgrey truncate">{file.original_name}</p>
            <span className="text-xs text-grey shrink-0">{formatBytes(file.size_bytes)}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={onDownload}
              className="p-1.5 rounded-md hover:bg-surface-active text-grey hover:text-primary transition-colors"
              aria-label="Download file"
            >
              <HiOutlineArrowDownTray className="w-4 h-4" />
            </button>
            {canShare && (
              <button
                onClick={onShare}
                className="p-1.5 rounded-md hover:bg-surface-active text-grey hover:text-secondary-dark transition-colors"
                aria-label="Share file"
              >
                <HiOutlineShare className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-surface-active text-grey hover:text-darkgrey transition-colors"
              aria-label="Close viewer"
              ref={closeButtonRef}
            >
              <HiOutlineXMark className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <FilePreviewSurface file={file} previewUrl={previewUrl} contentUrl={contentUrl} onDownload={onDownload} error={previewError} />
        </div>
      </div>
    </div>
  );
};
